import React, { createContext, useState, useContext, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import authService from "../services/auth";
import {
  markAuthenticated,
  clearAuthenticated,
  markOnboardingState,
} from "../services/api";
import pushSubscriptionService from "../services/pushSubscriptionService";
import { markOnboardingGuideSeen } from "../services/users";
import {
  bindPendingRemindersToUser,
  resetPendingReminders,
} from "../hooks/usePendingReminders";
import { resetFollowUpPreferences } from "../hooks/useFollowUpPopup";
import { clearPostDetailCache } from "../services/adminPosts";
import { clearProfessionHint } from "../utils/professionHint";
import { scheduleDeviceSignals } from "../services/deviceSignals";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Cache Storage entry the service worker uses to hand a pending
// notification-tap URL to the app across an iOS reload. It holds a route, not
// user data, and the worker's own activate step deliberately preserves it - so
// the logout sweep below preserves it too.
// Must match PUSH_NAV_CACHE in frontend/public/service-worker.js and App.js
// (the worker is a standalone script in public/, so the constant cannot be
// shared through a module - same mirroring precedent as App.js).
const PUSH_NAV_CACHE = "push-nav-pending-v1";

// Non-httpOnly hint cookie the backend sets alongside the real (httpOnly,
// /api-scoped) session cookies. Backlog #158 — the ONLY auth signal readable
// before React exists, which is what lets the head script in public/index.html
// hide the prerendered marketing snapshot from a signed-in visitor.
// Name and value are a cross-file contract: public/index.html reads the same
// name, the backend writes it. The value is the constant "1" and must never be
// enriched with a tier, an id, an email or a landing path — it is forgeable and
// readable by any XSS, so it may only ever mean "try the dashboard first".
const AUTH_HINT_COOKIE = "lf_auth";

// The one value the cookie may ever carry. See AUTH_HINT_COOKIE above for why
// it must stay a constant.
const AUTH_HINT_VALUE = "1";

// Matches NAME AND VALUE, each at a `;` boundary. Two things it rules out: an
// unrelated cookie whose name merely ends in ours (`xlf_auth`), and an EMPTY
// `lf_auth=` - the residue a delete leaves when it guesses the wrong domain or
// path. A name-only test reads that residue as a live hint, so the splash runs
// on every load and no retraction can ever clear it.
//
// Kept literally in sync with the inline head script in public/index.html,
// which reads the same cookie before this module exists. Change both together;
// src/__tests__/authBootHeadScript.test.js pins the shared source.
const AUTH_HINT_PATTERN = new RegExp(
  `(?:^|;\\s*)${AUTH_HINT_COOKIE}=${AUTH_HINT_VALUE}(?:\\s*;|$)`
);

/**
 * Is the auth hint cookie present right now?
 *
 * Presence of the constant value is the whole signal; nothing is decoded from
 * it.
 *
 * @returns {boolean} false whenever cookies are unreadable (privacy modes throw
 *                    on document.cookie) — failing to the anonymous path is the
 *                    pre-#158 behaviour and costs only the old flash.
 */
function readAuthHint() {
  try {
    return AUTH_HINT_PATTERN.test(document.cookie || "");
  } catch {
    return false;
  }
}

/**
 * Expire the hint cookie from the client.
 *
 * The backend owns the authoritative delete, but the client must be able to
 * retract a hint it has just disproved (a dead session, a failed logout call) -
 * otherwise every subsequent load of "/" splashes and bounces off /login.
 *
 * Deleting a cookie requires re-stating the attributes it was set with, and we
 * cannot see them from here, so all three plausible domain scopings are
 * expired. Same multi-variant shape as the cookie-consent utils, kept local on
 * purpose: this file must not depend on the consent module for an auth concern.
 *
 * `max-age=0` alongside the epoch `expires` on every variant: max-age wins
 * where both are understood and needs no correct client clock, while `expires`
 * remains for anything that predates it. A device whose clock is set to the
 * past honours neither on its own.
 *
 * @returns {void} never throws.
 */
function deleteAuthHintCookie() {
  try {
    const expired = "Thu, 01 Jan 1970 00:00:00 UTC";
    const host = window.location.hostname;
    document.cookie = `${AUTH_HINT_COOKIE}=; expires=${expired}; max-age=0; path=/;`;
    document.cookie = `${AUTH_HINT_COOKIE}=; expires=${expired}; max-age=0; path=/; domain=${host};`;
    document.cookie = `${AUTH_HINT_COOKIE}=; expires=${expired}; max-age=0; path=/; domain=.${host};`;
  } catch {
    /* Cookies unwritable — the hint expires with the session anyway. */
  }
}

/**
 * Drop every Cache Storage entry for this origin except the push-nav handoff.
 *
 * Why this belongs to logout: the service worker caches responses, and the
 * Cache API ignores Cache-Control - so whatever it stored sits on disk, outlives
 * the session, and is served back by the worker's offline branch to whoever uses
 * the device next. The worker no longer caches /api/* at all, but a device that
 * has not yet picked up that version still holds authenticated payloads under an
 * older cache name; sweeping by enumeration rather than by one known CACHE_NAME
 * is what covers that window, and it keeps this file free of a version constant
 * that would silently rot every time the worker's cache is bumped.
 *
 * Best-effort by design: callers must not await it. It needs no session, nothing
 * downstream depends on it, and a failure must never affect logging out. The
 * public app shell it also removes is re-cached on the next online load.
 *
 * @returns {Promise<void>} always resolves.
 */
async function clearServiceWorkerCaches() {
  try {
    if (typeof caches === "undefined") return;
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((name) => name !== PUSH_NAV_CACHE)
        .map((name) => caches.delete(name)),
    );
  } catch (error) {
    // Cache API unavailable, blocked by the browser, or mid-eviction.
    console.warn("Failed to clear service worker caches on logout:", error);
  }
}

/**
 * Revoke this device's Web Push subscription, best-effort.
 *
 * Without this a logged-out device keeps a live subscription and keeps
 * receiving another session's notifications. unsubscribe() is already a no-op
 * that returns true when this device has no subscription, and it swallows its
 * own errors - the try/catch here only guards against it being changed later.
 *
 * Call it BEFORE the session is revoked: it deletes the backend row over an
 * authenticated request. Callers must not await it (owner rule: a best-effort
 * cleanup never blocks the primary flow) - the browser-level revocation is
 * local and unconditional, and if the DELETE loses the race with cookie
 * clearing the stale row self-heals on the next push, when the push service
 * reports the endpoint gone.
 *
 * It deliberately does NOT write the deliberate-opt-out marker (see
 * PUSH_OPT_OUT_KEY in pushSubscriptionService). That omission is the whole
 * mechanism by which silentResubscribe() can tell "we revoked this at logout,
 * put it back" apart from "the user pressed Disable, leave it off" - so this
 * revocation costs the user nothing beyond the logged-out window. Only the
 * Settings Disable button may write that marker.
 *
 * The 'logout' surface is load-bearing for the same reason: the service
 * SUPPRESSES its push_unsubscribed event for it, so a healthy user signing out
 * is never counted as an opt-out.
 *
 * @returns {Promise<void>} always resolves.
 */
async function unsubscribePushQuietly() {
  try {
    await pushSubscriptionService.unsubscribe("logout");
  } catch (error) {
    console.warn("Failed to unsubscribe from push on logout:", error);
  }
}

/**
 * Forget the follow-up module caches (the page's pending reminders and the
 * reminder preferences). They are module-level, so they outlive a logout - and
 * a second user signing in on the same tab would otherwise see the first
 * user's reminder chips and inherit their remembered slot. Synchronous and
 * network-free.
 */
function resetFollowUpClientState() {
  resetPendingReminders();
  resetFollowUpPreferences();
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Read ONCE at init, synchronously, so RootRoute can decide on its very first
  // render. Deliberately NOT used to seed `isAuthenticated`: "the browser holds
  // a hint cookie" and "the server confirmed a session" are different facts,
  // and only the second one may unlock anything.
  const [authHint, setAuthHint] = useState(readAuthHint);

  // GUARD: Prevent concurrent checkAuth calls
  const isCheckingRef = useRef(false);

  // Retract the hint (state + cookie) once it has been disproved. Stable, so
  // every consumer below keeps its own stable identity.
  const clearAuthHint = useCallback(() => {
    deleteAuthHintCookie();
    setAuthHint(false);
  }, []);

  const checkAuth = useCallback(async () => {
    // Prevent concurrent auth checks
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      setLoading(true);

      if (authService.isUsingCookies()) {
        // =========================================================================
        // Cookie Mode: Always call API to validate cookies
        // The browser will automatically send httpOnly cookies with the request
        // =========================================================================
        try {
          // GET /auth/session ALWAYS returns 200 ({ authenticated, user }) and
          // silently refreshes an expired access token server-side. So a
          // logged-out visitor on a public page triggers NO 401 console error,
          // while a returning user whose access token expired is still revived
          // (parity with the old getProfile()+interceptor refresh-on-401 path).
          const session = await authService.getSession();
          if (session?.authenticated && session.user) {
            setUser(session.user);
            setIsAuthenticated(true);
            markAuthenticated();
          } else {
            // Anonymous — a normal logged-out result, not an error.
            setIsAuthenticated(false);
            setUser(null);
            // The server just disproved the hint: retract it, or "/" splashes
            // and bounces off /login on every future load.
            clearAuthHint();
          }
        } catch (error) {
          // /auth/session returns 200 even when logged out, so reaching here means
          // a genuine network/server error. Fail closed (logged out) without
          // clearing cookies, so a transient blip doesn't destroy a real session.
          console.warn("Session check failed (network/server issue):", error);
          setIsAuthenticated(false);
          setUser(null);
          // The hint is dropped here too. It costs a real user nothing: the
          // hint grants no access, and /auth/session re-sets it on its next
          // authenticated response. Keeping it would splash-and-bounce a
          // visitor whose session we could not verify.
          clearAuthHint();
        }
      } else {
        // =========================================================================
        // LocalStorage Mode: Check token exists, then validate with API
        // =========================================================================
        const token = authService.getAccessToken();

        if (token) {
          // Check if access token is expired or about to expire
          if (authService.isTokenExpired()) {
            // Try to refresh the access token using the refresh token
            const newAccessToken = await authService.refreshAccessToken();

            if (!newAccessToken) {
              // Refresh failed - session is truly expired
              setIsAuthenticated(false);
              setUser(null);
              // Disproved, exactly as in the cookie branch: an outliving hint
              // sends every future load of "/" through a splash and a bounce.
              clearAuthHint();
              setLoading(false);
              return;
            }
          }

          // Token valid (or just refreshed), verify with backend
          try {
            const userData = await authService.getProfile();
            setUser(userData);
            setIsAuthenticated(true);
            markAuthenticated();
          } catch (error) {
            if (error.response?.status === 401) {
              // Token invalid - clear and log out
              authService.removeTokens();
              setIsAuthenticated(false);
              setUser(null);
              // The server just disproved the hint; retract it with the tokens.
              clearAuthHint();
            } else {
              // Network/server error
              console.warn("Auth check failed (network/server issue):", error);
              setIsAuthenticated(false);
              // Same ruling as the cookie branch's network-error path: the
              // hint grants nothing and is re-set by the next authenticated
              // session response, so dropping it on an indeterminate outcome
              // costs a real user nothing - keeping it risks a splash-and-
              // bounce loop the header mode could never retract.
              clearAuthHint();
            }
          }
        } else {
          // No token in localStorage - user is not logged in
          setIsAuthenticated(false);
          setUser(null);
          // Nothing to verify against, so the hint is stale by definition.
          clearAuthHint();
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setUser(null);
      // Reachable from the header branch (refreshAccessToken and the token
      // reads above are not individually guarded). Same trade as the cookie
      // branch's catch: the hint grants nothing and is re-set by the next
      // authenticated response, so dropping it costs a real user nothing.
      clearAuthHint();
    } finally {
      // Auth check complete - reset loading and guard
      setLoading(false);
      isCheckingRef.current = false;
    }
  }, [clearAuthHint]); // clearAuthHint is stable, so checkAuth stays stable too

  /**
   * Silently re-sync the current user object from the backend WITHOUT toggling
   * the global `loading` flag. Use this after a mutation (added profession,
   * changed subscription, completed payment) to refresh `user` in place.
   *
   * Distinct from checkAuth(): checkAuth gates the whole app behind a
   * full-screen spinner (ProtectedRoute) while it determines auth status, which
   * UNMOUNTS the current page — correct on initial load, but as a post-action
   * refresh it destroys local component state (e.g. an open dialog) and causes
   * a jarring full-page flash. refreshUser keeps the page mounted: no spinner,
   * no remount.
   *
   * Non-fatal by design: a failed background refresh must never log the user
   * out or surface an error — we keep the existing session and try again later.
   *
   * Resolves `true` when a fresh user payload was applied and `false` when it
   * was not (the request failed, or the session read carried no user). It
   * never rejects. Callers that only need the side effect ignore the value;
   * the checkout's friend-code re-sync reads it to know whether the payload
   * can be trusted to say "referred or not" (L-01).
   */
  const refreshUser = useCallback(async () => {
    try {
      if (authService.isUsingCookies()) {
        const session = await authService.getSession();
        if (session?.authenticated && session.user) {
          setUser(session.user);
          setIsAuthenticated(true);
          markAuthenticated();
          return true;
        }
        return false;
      }
      const userData = await authService.getProfile();
      setUser(userData);
      setIsAuthenticated(true);
      markAuthenticated();
      return true;
    } catch (error) {
      console.warn("Silent user refresh failed (keeping current session):", error);
      return false;
    }
  }, []);

  // Key the follow-up caches to the signed-in user id. This covers every path
  // that changes WHO is signed in without passing through logout() - a session
  // found dead by checkAuth, a login as someone else. A LAYOUT effect on
  // purpose: it runs before any child's passive effect in the same commit, so
  // a lead card mounting for the new user fetches AFTER the reset, never
  // before it. (The API client's hard redirect to /login reloads the page,
  // which drops the module state on its own.)
  const userId = user?.id ?? null;
  const followUpOwnerRef = useRef(null);
  useLayoutEffect(() => {
    bindPendingRemindersToUser(userId);
    if (followUpOwnerRef.current !== userId) {
      followUpOwnerRef.current = userId;
      resetFollowUpPreferences();
    }
  }, [userId]);

  // The device beacon (services/deviceSignals.js - repeat-trial detection).
  // ONE call site for every way a user becomes signed in: it is keyed on the
  // signed-in user id, which every session door ends in - email/password
  // login, Google login or sign-up from /login, /register and the conversion
  // popup, and the boot-time session check. (An email sign-up holds NO session
  // after the code - the verify page sends its device fields with
  // /auth/verify-email instead, and this effect runs at the later login.)
  // `login`/`loginWithGoogle` set the context just before they set the user;
  // anything else (a boot with a live session) is "session".
  // Admins are skipped: their own devices are not evidence about customers.
  // Fire-and-forget and idle-scheduled - it never blocks render or throws.
  //
  // Cancellation is deliberately NOT an effect cleanup: a re-render or a route
  // change right after sign-in must not lose the scheduled send. Only a change
  // of WHO is signed in (sign-out, another account) or learning the user is an
  // admin cancels a send that has not gone out yet.
  //
  // Two things here look like leaks and are not:
  // - `pendingDeviceSignalRef` keeps its entry after the send has gone out.
  //   That stale `cancel` is a no-op by then, and the entry only stops a
  //   second schedule for the SAME user - which the beacon's own 12-hour
  //   throttle and per-page-load guard would refuse anyway. Sign-out (userId
  //   to null) or another account clears it at the top of the effect.
  // - There is no unmount cleanup. AuthProvider is the app root, so it
  //   unmounts only with the page itself, which takes any pending idle
  //   callback with it.
  const deviceSignalContextRef = useRef("session");
  const pendingDeviceSignalRef = useRef(null); // { userId, cancel }
  const isAdminUser = user?.is_admin === true;
  useEffect(() => {
    const pending = pendingDeviceSignalRef.current;
    if (pending && (pending.userId !== userId || isAdminUser)) {
      pendingDeviceSignalRef.current = null;
      try {
        pending.cancel();
      } catch {
        /* best-effort */
      }
    }
    if (!userId) return;
    const context = deviceSignalContextRef.current;
    deviceSignalContextRef.current = "session";
    if (isAdminUser) return;
    if (pendingDeviceSignalRef.current) return; // already scheduled for this user
    try {
      const cancel = scheduleDeviceSignals({ userId, context });
      pendingDeviceSignalRef.current = {
        userId,
        cancel: typeof cancel === "function" ? cancel : () => {},
      };
    } catch {
      /* best-effort: a beacon fault must never reach the auth tree */
    }
  }, [userId, isAdminUser]);

  // Call checkAuth once on mount
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only on mount, ignore checkAuth in deps since it's stable

  // Silently restore this device's push subscription on every login/auth.
  // Two jobs: it reactivates a backend row marked inactive after a transient
  // FCM/APNs failure, and it recreates the subscription that logout revoked -
  // otherwise push stayed dead on the device until the user re-enabled it by
  // hand. It never prompts for permission and never overrides a user who
  // deliberately disabled push; both guards live in silentResubscribe().
  useEffect(() => {
    if (isAuthenticated) {
      pushSubscriptionService.silentResubscribe();
    }
  }, [isAuthenticated]);

  const login = useCallback(async (credentials) => {
    const response = await authService.login(credentials);
    deviceSignalContextRef.current = "login";
    setIsAuthenticated(true);
    // Mark as authenticated for the interceptor (prevents loops)
    markAuthenticated();

    // Always fetch complete user profile after successful login
    // This ensures we have all fields (categories, profession_matching_status, etc.)
    // regardless of authentication mode
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      // If profile fetch fails, fall back to login response user data
      console.error("Failed to fetch user profile after login:", error);
      if (response.user) {
        setUser(response.user);
      }
    }

    return response;
  }, []);

  /**
   * Authenticate (or register) using a Google ID token.
   *
   * Delegates verification to the backend, then mirrors the post-login state
   * update from the regular login() method so the rest of the app is unaffected.
   *
   * @param {string} idToken - Raw Google ID token from GoogleAuthButton
   * @param {"login"|"register"} [mode="login"] - See authService.loginWithGoogle.
   *        Login callers (Login.js) must pass "login" so a missing account
   *        surfaces as a 404 error; register callers (Register.js) must pass
   *        "register" so the trial account is auto-created as expected.
   * @returns {Promise<Object>} Backend response including is_new_user and user profile
   */
  const loginWithGoogle = useCallback(async (idToken, mode = "login", options) => {
    // `options` carries the sign-up form's friend-code fields (register mode
    // only) through to the request builder - see authService.loginWithGoogle.
    const response = await authService.loginWithGoogle(idToken, mode, options);
    deviceSignalContextRef.current = response?.is_new_user === true ? "signup" : "login";
    setIsAuthenticated(true);
    markAuthenticated();

    // Fetch the full profile (same approach as regular login)
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch {
      // Fall back to the user object returned in the Google auth response
      if (response.user) {
        setUser(response.user);
      }
    }

    return response;
  }, []);

  /**
   * Sign this device out, and REPORT whether the server agreed.
   *
   * The local teardown below is unconditional - the hint cookie, the auth
   * state, the `authenticated` flag and the worker caches all go on every
   * path, because a tab holding credentials it has just tried to destroy is
   * worse than one holding none. What is NOT unconditional any more is the
   * claim: the result of `authService.logout()` is passed straight back, so a
   * caller can tell "revoked" from "we never reached the server" and say the
   * true thing instead of showing the logged-out shell for both.
   *
   * It does not throw, for the same reason the service does not: several
   * callers `await logout()` with no catch, and a rejection there would be an
   * unhandled rejection plus a half-finished teardown.
   *
   * @returns {Promise<{ok: boolean, reason: string, status?: number}>} see
   *   `services/auth.js::logout` for the five outcomes.
   */
  const logout = useCallback(async () => {
    // Started before the session is revoked so the backend DELETE still carries
    // it, and deliberately not awaited so it cannot add latency to - or fail -
    // the logout itself. See unsubscribePushQuietly() for the race analysis.
    unsubscribePushQuietly();

    let result = { ok: false, reason: "unreachable" };
    try {
      // Call backend to revoke refresh token
      result = (await authService.logout()) || { ok: true, reason: "revoked" };
    } catch (error) {
      // The service is documented never to reject; this is the belt to that
      // braces, and it must not leave `result` claiming success.
      console.error("Logout API call failed:", error);
      result = { ok: false, reason: "unreachable" };
    }
    // Outside the try/catch on purpose: logout continues even when the API call
    // fails, so the hint must be retracted on BOTH paths. A hint that outlives
    // the session sends the next visit to "/" through a splash and a bounce.
    clearAuthHint();
    resetFollowUpClientState();
    // Admin post details cached in memory must not outlive the session.
    clearPostDetailCache();
    // The conversion popup's profession hint is free text typed on THIS device;
    // it must not wait in storage for whoever signs in next.
    clearProfessionHint();
    // flushSync forces React to commit this state update synchronously before returning.
    // Without this, React 18 batches the update as a microtask — callers that navigate
    // immediately after await logout() would render route components with stale
    // isAuthenticated=true, causing a bounce to /dashboard then /login.
    flushSync(() => {
      setIsAuthenticated(false);
      setUser(null);
    });
    // Clear authenticated flag (allows clean login next time)
    clearAuthenticated();
    // Fire-and-forget: needs no session, and nothing here waits on it.
    clearServiceWorkerCaches();
    return result;
  }, [clearAuthHint]);

  const logoutAllSessions = useCallback(async () => {
    // Same device-level teardown as logout(): "sign out everywhere" ends this
    // device's session too, so this device must not keep serving cached
    // responses or receiving pushes. Only this browser's subscription is
    // revocable from here - unsubscribe() is scoped to its own endpoint.
    unsubscribePushQuietly();

    try {
      const result = await authService.logoutAllSessions();
      resetFollowUpClientState();
      clearPostDetailCache();
      clearProfessionHint();
      setIsAuthenticated(false);
      setUser(null);
      // Clear authenticated flag
      clearAuthenticated();
      clearAuthHint();
      clearServiceWorkerCaches();
      return result;
    } catch (error) {
      console.error("Logout all sessions failed:", error);
      // Still clear local state — including the hint: this device's session is
      // gone either way, and the caller still ends up logged out here.
      resetFollowUpClientState();
      clearPostDetailCache();
      clearProfessionHint();
      setIsAuthenticated(false);
      setUser(null);
      clearAuthenticated();
      clearAuthHint();
      clearServiceWorkerCaches();
      throw error;
    }
  }, [clearAuthHint]);

  /**
   * Record that the user has seen the in-app feature guide up to `version`.
   *
   * Updates the local user optimistically (so the guide won't re-open during
   * this session) then persists best-effort. The write is intentionally NOT
   * retried: a failure only means the guide may auto-open once on a future
   * session, which is harmless — graceful degradation beats added complexity
   * for a non-critical "seen" flag. The backend stores it monotonically.
   */
  const markGuideSeen = useCallback(async (version) => {
    setUser((prev) => {
      if (!prev) return prev;
      const current = prev.onboarding_guide_version || 0;
      return version > current ? { ...prev, onboarding_guide_version: version } : prev;
    });
    try {
      await markOnboardingGuideSeen(version);
    } catch (error) {
      console.warn("Failed to persist onboarding guide version:", error);
    }
  }, []);

  /**
   * Stamp an onboarding "seen" timestamp, optimistically then best-effort.
   *
   * Same contract as `markGuideSeen` above, for the same reason: the local user
   * is updated FIRST so the surface that just closed cannot reopen in this
   * session, and the write is deliberately NOT retried - a failure means one
   * extra showing on a future session, which is a smaller cost than the
   * complexity of a retry queue for a non-critical flag.
   *
   * The optimistic value is the CLIENT's clock, which is fine because nothing
   * reads these columns as instants: every consumer asks "is it null". The
   * server's own `now()` arrives with the next `/users/me`.
   *
   * @param {{welcomeSeen?: boolean, firstStepsDismissed?: boolean}} flags
   */
  const markOnboardingSeen = useCallback(async (flags = {}) => {
    const { welcomeSeen = false, firstStepsDismissed = false } = flags;
    if (!welcomeSeen && !firstStepsDismissed) return;

    const stampedAt = new Date().toISOString();
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      let changed = false;
      // COALESCE, client-side: the first stamp wins here exactly as it does in
      // the UPDATE, so a second close cannot move a recorded time.
      if (welcomeSeen && !prev.onboarding_welcome_seen_at) {
        next.onboarding_welcome_seen_at = stampedAt;
        changed = true;
      }
      if (firstStepsDismissed && !prev.onboarding_first_steps_dismissed_at) {
        next.onboarding_first_steps_dismissed_at = stampedAt;
        changed = true;
      }
      return changed ? next : prev;
    });

    try {
      await markOnboardingState({ welcomeSeen, firstStepsDismissed });
    } catch (error) {
      console.warn("Failed to persist onboarding state:", error);
    }
  }, []);

  // Memoized: context consumers bypass React.memo, so a fresh value object on
  // every AuthProvider render re-rendered every useAuth() consumer in the app
  // (including all 20 LeadCards). All functions above are useCallback-stable,
  // so this only changes identity when auth state actually changes.
  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      // Synchronous "this browser probably has a session" hint — see
      // AUTH_HINT_COOKIE above. Consumed by RootRoute (routing) and by the
      // conversion popup (SUPPRESSION only - a probably-signed-in visitor is
      // not shown a sign-up popup). It must never stand in for isAuthenticated
      // or unlock anything.
      authHint,
      loading,
      login,
      loginWithGoogle,
      logout,
      logoutAllSessions,
      checkAuth,
      refreshUser,
      markGuideSeen,
      markOnboardingSeen,
    }),
    [
      isAuthenticated,
      user,
      authHint,
      loading,
      login,
      loginWithGoogle,
      logout,
      logoutAllSessions,
      checkAuth,
      refreshUser,
      markGuideSeen,
      markOnboardingSeen,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
